<template>
    <div
        class="sip-file-dropzone"
        :class="{
            'is-dragover': dragover,
            'is-disabled': disabled,
            'has-file': !!fileName,
            'has-preview': !!previewSrc
        }"
    >
        <input
            :id="id || undefined"
            ref="input"
            class="sip-file-dropzone__input"
            type="file"
            :name="name || undefined"
            :accept="accept || undefined"
            :required="required"
            :disabled="disabled"
            :multiple="multiple"
            @change="onChange"
            @dragenter="onDragEnter"
            @dragover.prevent="onDragEnter"
            @dragleave="onDragLeave"
            @drop="onDragLeave"
        />
        <div class="sip-file-dropzone__body">
            <div v-if="previewSrc" class="sip-file-dropzone__thumb">
                <img :src="previewSrc" alt="">
            </div>
            <span v-else class="sip-file-dropzone__icon-wrap" aria-hidden="true">
                <em class="icon ni ni-upload-cloud sip-file-dropzone__icon"></em>
            </span>
            <div class="sip-file-dropzone__copy">
                <span class="sip-file-dropzone__title">{{ fileName || buttonLabel }}</span>
                <span class="sip-file-dropzone__hint">{{ hintText }}</span>
            </div>
        </div>
    </div>
</template>

<script>
    const IMAGE_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i

    export default {
        props: {
            id: { default: '' },
            name: { default: '' },
            buttonText: { default: '' },
            hint: { default: '' },
            accept: { default: '' },
            disabled: { type: Boolean, default: false },
            required: { type: Boolean, default: false },
            multiple: { type: Boolean, default: false },
            existingPreview: { default: '' },
            existingName: { default: '' }
        },
        data() {
            return {
                dragover: false,
                localName: '',
                localPreview: '',
                localSize: 0
            }
        },
        computed: {
            buttonLabel() {
                return this.buttonText
                    || (this.trans && this.trans('general.upload_document'))
                    || 'Upload Document'
            },
            fileName() {
                return this.localName || this.existingName || ''
            },
            previewSrc() {
                return this.localPreview || this.existingPreview || ''
            },
            hintText() {
                if (this.localName) {
                    const size = this.formatSize(this.localSize)
                    const replace = (this.trans && this.trans('general.upload_replace_hint')) || 'Click to replace or drag a new file'
                    return size ? (size + ' · ' + replace) : replace
                }
                if (this.hint) {
                    return this.hint
                }
                return (this.trans && this.trans('general.upload_drop_hint')) || 'Click to upload or drag a file'
            }
        },
        beforeDestroy() {
            this.revokePreview()
        },
        methods: {
            onDragEnter() {
                if (!this.disabled) {
                    this.dragover = true
                }
            },
            onDragLeave() {
                this.dragover = false
            },
            onChange(e) {
                this.dragover = false
                const files = (e.target && e.target.files) || []
                const file = files[0]
                if (!file) {
                    return
                }
                this.setLocalFile(file)
                this.$emit('selected', file)
                this.$emit('change', e)
            },
            setLocalFile(file) {
                this.revokePreview()
                this.localName = file.name || ''
                this.localSize = file.size || 0
                if (IMAGE_RE.test(file.name || '')) {
                    this.localPreview = URL.createObjectURL(file)
                } else {
                    this.localPreview = ''
                }
            },
            clear() {
                this.revokePreview()
                this.localName = ''
                this.localPreview = ''
                this.localSize = 0
                this.dragover = false
                if (this.$refs.input) {
                    this.$refs.input.value = ''
                }
            },
            open() {
                if (!this.disabled && this.$refs.input) {
                    this.$refs.input.click()
                }
            },
            revokePreview() {
                if (this.localPreview && this.localPreview.indexOf('blob:') === 0) {
                    try {
                        URL.revokeObjectURL(this.localPreview)
                    } catch (e) { /* ignore */ }
                }
            },
            formatSize(bytes) {
                const n = Number(bytes) || 0
                if (!n) {
                    return ''
                }
                if (n < 1024) {
                    return n + ' B'
                }
                if (n < 1048576) {
                    return (n / 1024).toFixed(n < 10 * 1024 ? 1 : 0) + ' KB'
                }
                return (n / 1048576).toFixed(1) + ' MB'
            }
        }
    }
</script>

<style>
.sip-file-dropzone {
    position: relative;
}
.sip-file-dropzone__input {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 2;
    font-size: 0;
}
</style>
